import "./AppAside.scss";
import {NavLink, useLocation, useNavigate} from "react-router-dom";
import OnlineMenusIcon from "./icons/OnlineMenusIcon";
import MyBrandIcon from "./icons/MyBrandIcon";
import LinkPagesIconBlack from "./icons/LinkPagesIconBlack";
import {env} from "../env/env";
import LocationDropdown from "./LocationDropdown";
import {LocationDto, MemberDto, MemberDtoPlanEnum} from "../generated/dashboard-client";
import React, {useCallback, useEffect, useState} from "react";
import {useRecoilValue} from "recoil";
import {
  memberState
} from "../state/atoms";

import SavedDesignsIcon from "./icons/SavedDesignsIcon";
import ItemLibraryIcon from "./icons/ItemLibraryIcon";

import {
  CreditCardOutlined,
  DollarCircleOutlined,
  PrinterOutlined,
  QuestionCircleOutlined,
  SettingOutlined,
  StarFilled,
  UserOutlined,
} from "@ant-design/icons";
import ChangeLocationIcon from "./icons/ChangeLocationIcon";
import {Button} from "antd";
import {OmInUseModal} from "./om/OmInUseModal";
import {HELP_CENTER_URL, homePaths, isTestEnv} from "../util/constants";
import {isAccountPath, isContentPath, isDSAPath, isLandingPath, isPrintPath, synchronousRequest} from "../util/utils";
import DisplayIconBlack from "./icons/DisplayIconBlack";
import ListingsIcon from "./icons/ListingsIcon";
import { MemberDtoRoleEnum } from "../generated/dashboard-client";
import { isProSubscription } from "../service/BillingService";
import DiningRDIcon from "./icons/DiningRDIcon";
import BrandKitIcon from "./icons/BrandKitIcon";

interface AppAsideProps {
  activeLocation: LocationDto;
  isMarketingBannerPage: boolean;
  users: MemberDto[];
}

enum AsideMode {
  Dashboard,
  Account,
  Hidden,
}

export default function AppAside({activeLocation, isMarketingBannerPage, users}: AppAsideProps) {
  const location = useLocation();
  const member = useRecoilValue(memberState);
  const isPro = isProSubscription(member);
  const [mode, setMode] = useState(AsideMode.Dashboard);
  const [isOoLoading, setOoLoading] = useState(false);
  const [isOoInUse, setOoInUse] = useState(false);
  const navigate = useNavigate();

  function goToTeam() {
    window.location.href = `${env.dashboardPath}/account/team`;
  }

  function goPro() {
    if (!member || !member.id) return;
    window.location.href = env.dashboardPath + "/account/plan";
  }

  const onOnlineMenuClick = useCallback(() => {
    if (!isProSubscription(member)) {
      navigate(`${env.dashboardPath}/online-menus/`);
      return;
    }

    setOoLoading(true);
    setTimeout(() => {
      const resp = synchronousRequest(`/app/online/location/stats/${activeLocation.internalId!}?oo=true`);
      let location: any | null = null;
      if (resp) {
        try {
          location = JSON.parse(JSON.parse(resp).data).location;
        } catch (e) {
          console.error(e);
        }
      }

      if (location?.inUse) {
        setOoInUse(true);
        setOoLoading(false);
      } else {
        const base = isTestEnv ? 'https://test.mhmfun.com' : 'https://ohbz.com';//window.location.href.indexOf("staging.") > -1 ? "https://www.musthavemenus.com" : env.basePath;
        const tkn = window.localStorage.getItem("tkn");
        window.open(`${base}${env.ooContextPath}?businessId=${activeLocation.internalId}&tkn=${tkn}`, '_blank');
        setOoLoading(false);
      }
    }, 200);
  }, [activeLocation.internalId, navigate, member])

  useEffect(() => {
    const {pathname} = location;

    if (isPrintPath(pathname) || isLandingPath(pathname) || isContentPath(pathname) || isDSAPath(pathname)) {
      setMode(AsideMode.Hidden);
    } else if (isAccountPath(pathname)) {
      setMode(AsideMode.Account);
    } else {
      setMode(AsideMode.Dashboard);
    }
  }, [location]);

  return (
    <>
      {mode !== AsideMode.Hidden && (
        <aside className={"main"} style={{top: !isMarketingBannerPage ? '80px' : ''}}>
          {mode === AsideMode.Dashboard && (
            <>
              <LocationDropdown location={activeLocation} users={users} />
              <nav>
                <ul>
                  <li>
                    <NavLink
                      to={`${env.dashboardPath}/designs/${activeLocation.internalId}`}
                      className={
                        homePaths.includes(window.location.pathname)
                          ? "active"
                          : ""
                      }
                    >
                      <SavedDesignsIcon/>
                      Saved Designs
                    </NavLink>
                  </li>

                  {!member.viewOnly && !member.updateDrd && member.showItemLibrary && (
                    <li className={!isPro ? "gray-out disallowed" : ""}>
                      <NavLink onClick={(event) => {
                          if (!isPro) {
                            event.preventDefault();
                          }
                        }}
                        aria-disabled={!isPro}
                        to={`${env.dashboardPath}/item-library`}
                        className={
                          window.location.pathname.indexOf("item-library") > -1
                            ? "active"
                            : ""
                        }
                      >
                        <ItemLibraryIcon />
                        <span style={{ margin: 0 }}>Item Library</span>
                        {!isPro && (
                          <img
                            src="https://timber.mhmcdn.com/site/marketing/icons/ProPricingStar.svg"
                            alt="pro star"
                          />
                        )}
                      </NavLink>
                    </li>
                  )}

                  {!member.viewOnly && !member.itemLibraryOnly && !member.dataOnly && !member.updateDrd &&
                  <li className={!isProSubscription(member) ? 'gray-out' : ''}>
                    <NavLink to={`${env.dashboardPath}/dsa/manage`}>
                      <DisplayIconBlack/>
                      <span style={{margin: 0}}>Display</span>
                      {!isProSubscription(member) &&
                      <img src="https://timber.mhmcdn.com/site/marketing/icons/ProPricingStar.svg" alt="pro star"/>}
                    </NavLink>
                  </li>}

                  {!member.viewOnly && !member.itemLibraryOnly && !member.dataOnly && !member.updateDrd &&
                  <li className={!isProSubscription(member) ? 'gray-out' : ''} style={{display: 'none'}}>
                    <NavLink
                      className={location.pathname.indexOf("listings") > -1 ? "active" : ""}
                      to={`${env.dashboardPath}/listings/${activeLocation.internalId}`}
                    >
                      <ListingsIcon/>
                      <span>Listings</span>
                      {!isProSubscription(member) &&
                      <img src="https://timber.mhmcdn.com/site/marketing/icons/ProPricingStar.svg" alt="pro star"/>}
                    </NavLink>
                  </li>}

                  {!member.viewOnly && !member.itemLibraryOnly && !member.dataOnly && !member.updateDrd &&
                  <li className={!isProSubscription(member) ? 'gray-out' : ''}>
                    <Button
                      onClick={onOnlineMenuClick}
                      type={"link"}
                      loading={isOoLoading}
                      className={location.pathname.indexOf("online-menus") > -1 ? "active" : ""}
                    >
                      {!isOoLoading && <OnlineMenusIcon/>}
                      <span>Online Menus</span>
                      {!isProSubscription(member) &&
                      <img src="https://timber.mhmcdn.com/site/marketing/icons/ProPricingStar.svg" alt="pro star"/>}
                    </Button>
                  </li>}
                  {!member.viewOnly && !member.itemLibraryOnly && !member.dataOnly && !member.updateDrd &&
                  <li className={!isProSubscription(member) ? 'gray-out' : ''}>
                    <NavLink
                      className={location.pathname.indexOf("link-pages") > -1 ? "active" : ""}
                      to={`${env.dashboardPath}/link-pages/${activeLocation.internalId}`}
                    >
                      <LinkPagesIconBlack/>
                      <span>Link Pages</span>
                      {!isProSubscription(member) &&
                      <img src="https://timber.mhmcdn.com/site/marketing/icons/ProPricingStar.svg" alt="pro star"/>}
                    </NavLink>
                  </li>}
                  {!member.viewOnly && !member.itemLibraryOnly && !member.dataOnly && !member.updateDrd &&
                  <li>
                    <NavLink
                      className={location.pathname.indexOf("brand") > -1 ? "active" : ""}
                      to={`${env.dashboardPath}/brand/${activeLocation.internalId}`}
                    >
                      <MyBrandIcon/>
                      <span>Brand</span>
                    </NavLink>
                  </li>}
                  <li>
                    <a href={HELP_CENTER_URL} target="_blank" rel="noreferrer">
                      <QuestionCircleOutlined/>
                      Help Center
                    </a>
                  </li>
                </ul>
              </nav>

              <footer>
                {(member.owner || member.role === "Admin" as MemberDtoRoleEnum) && (
                  <div className={"btn-container"}>
                    {!isProSubscription(member) ? (
                      <Button onClick={goPro} className={"go-pro"}>
                        <StarFilled/>
                        Upgrade Now
                      </Button>
                    ) : (
                      <Button onClick={goToTeam} className={"secondary-two add-team"}>
                        <UserOutlined/>
                        Add Your Team
                      </Button>
                    )}
                  </div>
                )}
              </footer>
            </>
          )}

          {mode === AsideMode.Account && (
            <>
              <nav className={"account-aside"}>
                <ul>
                  <li>
                    <NavLink
                      className={"user-info"}
                      to={env.dashboardPath + "/account/users"}
                    >
                      <SettingOutlined/>
                      User Info
                    </NavLink>
                  </li>
                  {member.plan !== MemberDtoPlanEnum.Free && (member.owner || member.role === "Admin") && (
                    <li>
                      <NavLink
                        className={"billing-info"}
                        to={env.dashboardPath + "/account/billing"}
                      >
                        <CreditCardOutlined/>
                        Billing Info
                      </NavLink>
                    </li>
                  )}
                  {(member.owner || member.role === "Admin") && (
                    <li>
                      <NavLink
                        className={"subscription"}
                        to={env.dashboardPath + "/account/plan"}
                      >
                        <DollarCircleOutlined/>
                        Subscription
                      </NavLink>
                    </li>
                  )}
                  {(member.owner || member.role === "Admin") && (
                    <li className={!isProSubscription(member) && !member.diningRDIntegration ? 'gray-out' : ''}>
                      <NavLink
                        className={"locations"}
                        to={env.dashboardPath + "/account/locations"}
                      >
                        <ChangeLocationIcon/>
                        <span>Locations</span>
                        {!isProSubscription(member) && !member.diningRDIntegration &&
                        <img src="https://timber.mhmcdn.com/site/marketing/icons/ProPricingStar.svg" alt="pro star"/>}
                      </NavLink>
                    </li>
                  )}
                  {(member.owner || member.role === "Admin") && (
                    <li className={!isProSubscription(member) && !member.diningRDIntegration ? 'gray-out' : ''}>
                      <NavLink
                        className={"team"}
                        to={env.dashboardPath + "/account/team"}
                      >
                        <UserOutlined/>
                        <span>Team</span>
                        {!isProSubscription(member) && !member.diningRDIntegration &&
                        <img src="https://timber.mhmcdn.com/site/marketing/icons/ProPricingStar.svg" alt="pro star"/>}
                      </NavLink>
                    </li>
                  )}
                  {(member.owner || member.role === "Admin" || (member as any).manageBrand) && (
                  <li>
                    <NavLink
                      className={({ isActive }) =>
                        "brand-kit" + (isActive || location.pathname.startsWith(env.dashboardPath + "/account/brand-management") ? " active" : "")
                      }
                      to={env.dashboardPath + "/account/brand-management"}
                    >
                      <BrandKitIcon/>
                      <span>Brand Management</span>
                    </NavLink>
                  </li>
                  )}
                  {(member.owner || member.role === "Admin") && (
                    <li>
                      <NavLink
                        className={"print-history"}
                        to={env.dashboardPath + "/account/print"}
                      >
                        <PrinterOutlined/>
                        Print History
                      </NavLink>
                    </li>
                  )}
                  {(member.owner || member.role === "Admin") && member.diningRDIntegration && (
                    <li>
                      <NavLink
                        className={"drd"}
                        to={env.dashboardPath + "/account/drd"}
                      >
                        <DiningRDIcon/>
                        DiningRD Integration
                      </NavLink>
                    </li>
                  )}
                </ul>
              </nav>
            </>
          )}
        </aside>
      )}

      <OmInUseModal
        isVisible={isOoInUse}
        onClose={() => {
          setOoInUse(false);
          setOoLoading(false);
        }}
      />
    </>
  );
}
